// CRM / CTMS / Spend toggle rules for the Organizations V3 grid.
// Mirrors the v2 HCO search (pages/Search/components/Result.tsx + SearchResults.tsx)
// so both pages agree on who sees and who can change each flag.
import hco from '../../lookup/hco.json';
import type { HcoSearchRowV3 } from '../../api/organizationsV3Api';

export type FlagKind = 'Spend' | 'CRM' | 'CTMS';

export interface FlagUser {
    role?:        string;
    team?:        string;
    terr?:        string;
    isDermAdmin?: string;
}

export type FlagOverrides = Record<string, Partial<Record<FlagKind, boolean>>>;

export interface ToggleState {
    active:   boolean;
    disabled: boolean;
    global:   boolean;
}

interface ResultTitle {
    title:  string;
    column: string;
    roles:  string[];
    teams:  string[];
}

const EXCLUDE_CRM_ROLES = ['PHARMFORCE', 'ENVISION'];
const ALWAYS_IN_TERRITORY_TEAMS = ['51', '52', '53', '54'];

const titleFor = (column: string) =>
    (hco.resultTitles as ResultTitle[]).find(t => t.column === column);

export function visibleFlagColumns(user: FlagUser | undefined): FlagKind[] {
    const role = user?.role ?? '';
    const team = user?.team ?? '';
    const cols: FlagKind[] = [];

    if (titleFor('CTMS_FLAG')?.roles.includes(role)) cols.push('CTMS');

    const crm = titleFor('UNIVERSE_FLAG_TM');
    const crmVisible = !!crm
        && (crm.roles.includes('ALL') || crm.roles.includes(role) || crm.teams.includes(team))
        && !EXCLUDE_CRM_ROLES.includes(role);
    if (crmVisible) cols.push('CRM');

    cols.push('Spend');
    return cols;
}

export const rowKey = (row: HcoSearchRowV3) => String(row.orgId ?? '');

const isY = (value: unknown) => value === 'Y';

// v2 builds `new RegExp('^' + user.terr)`, so an undefined territory matches nothing
// and an empty one matches everything; String() keeps that behavior.
const inTerritory = (value: string | null | undefined, terr: string | undefined) =>
    !!value && value.startsWith(String(terr));

export function isDermRequestor(user: FlagUser | undefined): boolean {
    return user?.role === 'PRT' || user?.role === 'COMMERCIAL DERM' || user?.isDermAdmin === 'true';
}

export function toggleState(
    kind: FlagKind,
    row: HcoSearchRowV3,
    user: FlagUser | undefined,
    overrides: FlagOverrides,
): ToggleState {
    const local = overrides[rowKey(row)] ?? {};

    if (kind === 'Spend') {
        const active = local.Spend ?? isY(row.aggspendFlag);
        // v2: Spend can only be added from search, never removed.
        return { active, disabled: active, global: false };
    }

    if (kind === 'CTMS') {
        return { active: local.CTMS ?? isY(row.ctmsFlag), disabled: false, global: false };
    }

    const role = user?.role ?? '';
    const isAdmin = role === 'ADMIN';
    const notOverridden = row.mdmOverride !== 'X';
    const matchesTerritory =
        ALWAYS_IN_TERRITORY_TEAMS.includes(user?.team ?? '')
        || inTerritory(row.terrId, user?.terr)
        || (!notOverridden && inTerritory(row.mdmTerrId, user?.terr));
    const teamFlag = isY((row as unknown as Record<string, unknown>)[`universeFlagTm${user?.team ?? ''}`]);

    const disabled = !isAdmin && !matchesTerritory && role !== 'PRT' && role !== 'COMMERCIAL DERM';
    let global = !isAdmin && !matchesTerritory && teamFlag && notOverridden;
    if (isDermRequestor(user) && teamFlag) global = false;

    // v2 seeds its local CRM state from UNIVERSE_FLAG, so that is what the toggle shows.
    return { active: local.CRM ?? isY(row.universeFlag), disabled, global };
}
